import { FC } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Users, Database, Lightbulb, Building, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
interface Department {
  name: string;
  participants: number;
}
interface NetworkStats {
  totalParticipants: number;
  activeContributors: number;
  knowledgeBases: number;
  totalSolutions: number;
  departments: Department[];
}
interface KnowledgeNetworkStatsProps {
  stats: NetworkStats;
}
const KnowledgeNetworkStats: FC<KnowledgeNetworkStatsProps> = ({
  stats
}) => {
  const activePercentage = Math.round(stats.activeContributors / stats.totalParticipants * 100);
  return <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex flex-col items-center text-center">
            <Users className="h-8 w-8 text-blue-600 mb-2" />
            <h3 className="text-2xl font-bold">{stats.totalParticipants.toLocaleString()}</h3>
            <p className="text-sm text-gray-600">Profiles</p>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex flex-col items-center text-center">
            <Users className="h-8 w-8 text-green-600 mb-2" />
            <h3 className="text-2xl font-bold">{stats.activeContributors.toLocaleString()}</h3>
            <p className="text-sm text-gray-600">Active Creators</p>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex flex-col items-center text-center">
            <Database className="h-8 w-8 text-purple-600 mb-2" />
            <h3 className="text-2xl font-bold">{stats.knowledgeBases.toLocaleString()}</h3>
            <p className="text-sm text-gray-600">Knowledge Bases</p>
          </div>
        </Card>
        
        <Link to="/ispai-marketplace" className="block">
          <Card className="p-4 cursor-pointer hover:bg-gray-50 hover:shadow-md transition-all group">
            <div className="flex flex-col items-center text-center">
              <div className="relative">
                <Lightbulb className="h-8 w-8 text-amber-600 mb-2" />
                <ExternalLink className="h-4 w-4 absolute -right-3 -top-1 opacity-0 group-hover:opacity-100 transition-opacity text-amber-600" />
              </div>
              <h3 className="text-2xl font-bold">{stats.totalSolutions.toLocaleString()}</h3>
              <p className="text-sm text-gray-600 group-hover:text-amber-600 transition-colors">Marketplace</p>
            </div>
          </Card>
        </Link>
      </div>
      
      <Card>
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Building className="mr-2 h-5 w-5" />
            Participation by Department
          </h3>
          
          <div className="space-y-4">
            {stats.departments.map((dept, index) => {
            const percentage = Math.round(dept.participants / stats.totalParticipants * 100);
            return <div key={index}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">{dept.name}</span>
                    <span className="text-sm text-gray-600">{dept.participants} participants ({percentage}%)</span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                </div>;
          })}
          </div>
          
          <div className="mt-6">
            <h4 className="text-sm font-medium mb-2">Network Activity</h4>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Progress value={activePercentage} className="h-2" />
              </div>
              <span className="text-sm text-gray-600">
                {activePercentage}% Active Contributors
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
};
export default KnowledgeNetworkStats;